import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Checkbox, Tooltip } from '@ds';
import { RouteResourceCard } from '@lp/features/RoutePlanner/components/CaseInformation/RouteResourceCard';
import { useLegFormResource } from '@lp/features/RoutePlanner/components/RouteLegForm/hooks';
import { CommonLegResourcesSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { CaseResourceType, OrganType, RandomizationResultType } from '@/api/apis/case';
import { getFullName } from '@/utils/helpers';
import { useWatchFields } from '@/hooks/useWatchFields';
import { RouteLegTypeEnum } from '@/api/apis/route';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';

export const FormResourceList = () => {
  const { isResourcesEdit: isMainDataEdit } = useLegFormContext();
  const { control, setValue } = useFormContext<CommonLegResourcesSchemaValues>();
  const { resources, legType } = useWatchFields<CommonLegResourcesSchemaValues>(['resources', 'legType'] as const);
  const { specialistsData, surgeonsData, devicesData } = useLegFormResource();
  const { Crew: crews } = useRouteResources();
  const { trialProgram, randomization } = useBaseCaseDetails();

  const isCommercial = legType === RouteLegTypeEnum.CommercialFlightAviaLeg;
  const isAccommodation = legType === RouteLegTypeEnum.AccommodationLeg;

  const onResourceSelect = (resourceType?: CaseResourceType, resourceId?: string) => {
    if (!resourceId || !resourceType) return;

    const hasResource = resources.find((item) => item.resourceId === resourceId);

    setValue(
      'resources',
      hasResource
        ? resources.filter((item) => item.resourceId !== resourceId)
        : [...resources, { resourceId, resourceType }],
      {
        shouldValidate: true,
      },
    );
  };

  const showScsCheckbox = useMemo(() => {
    const isTrial = !!trialProgram;
    const isScsResult = randomization?.result === RandomizationResultType.SCS;

    return isTrial && isScsResult;
  }, [trialProgram, randomization]);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-l-sb">Resources involved</h1>
      <div className={`relative flex flex-row gap-2 ${isAccommodation ? 'flex-wrap' : 'overflow-auto'}`}>
        <Controller
          name="resources"
          control={control}
          render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid } }) => (
            <>
              {specialistsData.map((i) => (
                <Tooltip
                  key={i.id || 'specialist-empty'}
                  content={i.subTitle || 'Clinical Specialist is not assigned'}
                  delay={0}
                  showArrow
                  isDisabled={!isMainDataEdit}
                >
                  <div className="w-56 shrink-0">
                    <RouteResourceCard
                      hasResource={i.isAssigned}
                      imageUrl={i.imageUrl}
                      title={i.title}
                      statusType={i.statusType}
                      subTitle={i.subTitle}
                      statusText={i.statusText}
                      showAssignButton={false}
                    >
                      <Checkbox
                        {...field}
                        value="clinicalSpecialist"
                        isInvalid={invalid}
                        isSelected={value.some((item) => item.resourceId === i.id)}
                        isDisabled={!isMainDataEdit || !i.isAssigned}
                        className="p-0 flex justify-end"
                        size="md"
                        onValueChange={() => onResourceSelect(i.type, i.id)}
                      />
                    </RouteResourceCard>
                  </div>
                </Tooltip>
              ))}
              {!isCommercial &&
                devicesData.map((i) => (
                  <Tooltip
                    key={i.id || 'device-empty'}
                    content={i.subTitle || 'OCS Device is not assigned'}
                    delay={0}
                    showArrow
                    isDisabled={!isMainDataEdit}
                  >
                    <div className="w-56 shrink-0">
                      <RouteResourceCard
                        type="device"
                        hasResource={i.isAssigned}
                        imageUrl={i.imageUrl}
                        title={i.title}
                        statusType={i.statusType}
                        subTitle={i.subTitle}
                        statusText={i.statusText}
                        showAssignButton={false}
                        organType={i.organType as OrganType}
                      >
                        <Checkbox
                          {...field}
                          value="device"
                          isInvalid={invalid}
                          isSelected={value.some((item) => item.resourceId === i.id)}
                          isDisabled={!isMainDataEdit || !i.isAssigned}
                          className="p-0 flex justify-end"
                          size="md"
                          onValueChange={() => onResourceSelect(i.type, i.id)}
                        />
                      </RouteResourceCard>
                    </div>
                  </Tooltip>
                ))}
              {surgeonsData.map((i) => (
                <Tooltip
                  key={i.id || 'surgeon-empty'}
                  content={i.subTitle || 'Surgeon is not assigned'}
                  delay={0}
                  showArrow
                  isDisabled={!isMainDataEdit}
                >
                  <div className="w-56 shrink-0">
                    <RouteResourceCard
                      hasResource={i.isAssigned}
                      imageUrl={i.imageUrl}
                      title={i.title}
                      statusType={i.statusType}
                      subTitle={i.subTitle}
                      statusText={i.statusText}
                      showAssignButton={false}
                    >
                      <Checkbox
                        {...field}
                        value="surgeons"
                        isInvalid={invalid}
                        isSelected={value.some((item) => item.resourceId === i.id)}
                        isDisabled={!isMainDataEdit || !i.isAssigned}
                        className="p-0 flex justify-end"
                        size="md"
                        onValueChange={() => onResourceSelect(i.type, i.id)}
                      />
                    </RouteResourceCard>
                  </div>
                </Tooltip>
              ))}
              {isAccommodation &&
                crews.map((i) => (
                  <div key={i.resourceId} className="w-56 shrink-0">
                    <RouteResourceCard
                      hasResource={false}
                      title={getFullName(i.firstName || '', i.lastName || '')}
                      subTitle="TMDX Pilot"
                      showAssignButton={false}
                    >
                      <Checkbox
                        {...field}
                        value="crews"
                        isInvalid={invalid}
                        isSelected={value.some((item) => item.resourceId === i.resourceId)}
                        isDisabled={!isMainDataEdit}
                        className="p-0 flex justify-end"
                        size="md"
                        onValueChange={() => onResourceSelect(CaseResourceType.Crew, i.resourceId)}
                      />
                    </RouteResourceCard>
                  </div>
                ))}
            </>
          )}
        />
      </div>
      {!isCommercial && showScsCheckbox && (
        <div className="w-full">
          <Controller
            name="hasSCS"
            control={control}
            render={({ field: { ref, value, ...field } }) => (
              <Checkbox size="sm" {...field} value="hasSCS" isDisabled={!isMainDataEdit} isSelected={!!value}>
                The static cold storage with the organ on this leg
              </Checkbox>
            )}
          />
        </div>
      )}
    </div>
  );
};
